import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCoupon, addFavorite, removeFavorite, redeemCoupon } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Copy, Heart, ExternalLink, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";

export default function CouponDetail() {
  const { id } = useParams();
  const [coupon, setCoupon] = useState(null);
  const [copied, setCopied] = useState(false);
  const { user, favIds, refreshFavs, startGoogleLogin } = useAuth();

  useEffect(() => {
    fetchCoupon(id).then(setCoupon).catch(() => setCoupon(false));
  }, [id]);

  useSeo({
    title: coupon
      ? `${coupon.discount_label} at ${coupon.store} — ${coupon.title} · The Couponer`
      : "Coupon · The Couponer",
    description: coupon
      ? `${coupon.title}. ${(coupon.description || "").slice(0, 100)} · verified ${coupon.store} promo code on The Couponer.`.slice(0, 155)
      : undefined,
    canonical: coupon ? `https://thecouponer.com/coupon/${coupon.slug || coupon.id}` : undefined,
    keywords: coupon
      ? `${coupon.store} coupon, ${coupon.store} promo code, ${coupon.store} discount, ${coupon.category} deals, ${coupon.discount_label}`
      : undefined,
    // JSON-LD Offer schema — powers Google's coupon-rich result cards
    // (they render discount %, expiry, and code preview directly in SERP).
    jsonLd: coupon
      ? {
          "@context": "https://schema.org",
          "@type": "Offer",
          "name": coupon.title,
          "description": coupon.description || coupon.title,
          "url": `https://thecouponer.com/coupon/${coupon.slug || coupon.id}`,
          "category": coupon.category,
          "seller": {
            "@type": "Organization",
            "name": coupon.store,
            "url": coupon.store_url,
          },
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          ...(coupon.expires_at && { "validThrough": coupon.expires_at }),
          ...(coupon.code && {
            "priceSpecification": {
              "@type": "PriceSpecification",
              "description": `Use code ${coupon.code} at checkout`,
            },
          }),
          "eligibleRegion": { "@type": "Country", "name": "United States" },
        }
      : undefined,
  });

  if (coupon === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="serif text-4xl">Coupon not found.</p>
        <Link to="/" className="mono text-xs tracking-widest underline mt-4 inline-block">
          BACK TO FEED
        </Link>
      </div>
    );
  }

  if (!coupon) return <div className="mono text-xs p-10">LOADING...</div>;

  const isFav = favIds?.has(coupon.id);

  const copyCode = async () => {
    if (!coupon.code) return;
    await navigator.clipboard.writeText(coupon.code);
    setCopied(true);
    toast.success(`Code ${coupon.code} copied`);
    redeemCoupon(coupon.id).catch(() => {});
    setTimeout(() => setCopied(false), 2200);
  };

  const toggleFav = async () => {
    if (!user) {
      toast("Sign in to save deals", {
        action: { label: "Sign in", onClick: startGoogleLogin },
      });
      return;
    }
    if (isFav) {
      await removeFavorite(coupon.id);
      toast.success("Removed");
    } else {
      await addFavorite(coupon.id);
      toast.success("Saved");
    }
    refreshFavs();
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-10" data-testid="coupon-detail-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] hover:underline mb-6"
        data-testid="back-to-feed"
      >
        <ArrowLeft className="w-3 h-3" /> BACK TO FEED
      </Link>

      <article className="border-2 border-dashed border-black bg-white p-8 md:p-12 hard-shadow">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] text-[#5c5c5c] mb-4">
          <span>{coupon.kind.toUpperCase()}</span>
          <span>·</span>
          <span>{coupon.category?.toUpperCase()}</span>
          <span>·</span>
          <span>@ {coupon.store.toUpperCase()}</span>
        </div>

        <h1 className="serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-4">
          {coupon.title}
        </h1>

        <div className="my-4">
          <span className="inline-block bg-black text-[#f6f4f0] px-4 py-2 text-sm tracking-[0.3em] font-semibold">
            {coupon.discount_label}
          </span>
        </div>

        <p className="text-base leading-relaxed text-[#3a3a38] mb-6">
          {coupon.description}
        </p>

        <div className="cut-divider my-6" />

        {coupon.code ? (
          <div className="mb-6">
            <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-2">PROMO CODE</div>
            <div className="border-2 border-dashed border-black bg-[#f6f4f0] px-4 py-4 flex items-center justify-between max-w-md">
              <span className="mono text-2xl tracking-widest font-semibold">
                {coupon.code}
              </span>
              <button
                onClick={copyCode}
                data-testid="detail-copy-code"
                className="flex items-center gap-2 border border-black px-3 py-1.5 text-xs tracking-[0.2em] hover:bg-black hover:text-[#f6f4f0]"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? "COPIED" : "COPY"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-6 border border-black bg-[#f6f4f0] p-4 max-w-md">
            <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-1">NO CODE NEEDED</div>
            <div className="mono">Discount applied automatically at checkout.</div>
          </div>
        )}

        {coupon.terms && (
          <div className="mb-6 max-w-2xl">
            <div className="text-[10px] tracking-[0.3em] text-[#5c5c5c] mb-1">TERMS & CONDITIONS</div>
            <p className="text-sm leading-relaxed">{coupon.terms}</p>
          </div>
        )}

        <div className="cut-divider my-6" />

        <div className="flex flex-wrap items-center gap-3">
          <a
            href={coupon.store_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="detail-visit-store"
            onClick={() => redeemCoupon(coupon.id).catch(() => {})}
          >
            <Button className="rounded-none bg-[#D34836] hover:bg-[#b73824] text-white tracking-[0.25em] px-6 py-6 hard-shadow">
              VISIT {coupon.store.toUpperCase()}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </a>
          <Button
            onClick={toggleFav}
            data-testid="detail-fav-btn"
            variant="outline"
            className={`rounded-none border-2 border-black tracking-[0.25em] px-6 py-6 hard-shadow-sm ${
              isFav ? "bg-[#D34836] text-white border-[#D34836]" : "bg-white hover:bg-black hover:text-white"
            }`}
          >
            <Heart className={`w-4 h-4 mr-2 ${isFav ? "fill-current" : ""}`} />
            {isFav ? "SAVED" : "SAVE"}
          </Button>
        </div>

        <div className="mt-8 text-[11px] tracking-[0.2em] text-[#5c5c5c] flex flex-wrap gap-6">
          <div>
            <div className="mb-1 opacity-70">EXPIRES</div>
            <div>{coupon.expires_at || "—"}</div>
          </div>
          <div>
            <div className="mb-1 opacity-70">CLICKS</div>
            <div>{coupon.redemptions}</div>
          </div>
          <div>
            <div className="mb-1 opacity-70">STATUS</div>
            <div>{coupon.verified ? "VERIFIED" : "PENDING"}</div>
          </div>
        </div>
      </article>
    </div>
  );
}
